import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Eye, Search } from 'lucide-react';
import { format } from 'date-fns';
import api from '../../utils/api';
import DataTable from '../../components/ui/DataTable';
import StatusChip from '../../components/ui/StatusChip';
import { useAuth } from '../../context/AuthContext';

const InwardList = () => {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filters, setFilters]   = useState({ status: '', search: '' });

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.search) params.search = filters.search;
    api.get('/inward', { params })
      .then((r) => setReceipts(r.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filters]);

  const columns = [
    {
      label: 'Inward No.', key: 'inwardNumber',
      render: (r) => (
        <span className="font-mono text-zip-red font-medium text-xs">{r.inwardNumber}</span>
      ),
    },
    {
      label: 'Invoice No.', key: 'invoiceNumber',
      render: (r) => (
        <span className="font-mono text-xs text-zip-muted">{r.invoiceNumber}</span>
      ),
    },
    { label: 'Supplier', key: 'supplier', render: (r) => r.supplier?.name ?? '—' },
    {
      // Arrival date only — deliberately not invoiceDate. Receipts saved without an
      // arrival timestamp show a dash rather than silently falling back to another date.
      label: 'Arrival Date', key: 'arrivalDateTime',
      render: (r) => (
        <span className="font-mono text-xs text-zip-muted">
          {r.arrivalDateTime ? format(new Date(r.arrivalDateTime), 'dd MMM yyyy') : '—'}
        </span>
      ),
    },
    { label: 'Items', key: 'totalItems', render: (r) => <span className="font-mono">{r.totalItems}</span> },
    { label: 'Status', key: 'status', render: (r) => <StatusChip status={r.status} /> },
    { label: 'Received By', key: 'receivedBy', render: (r) => r.receivedBy?.name ?? '—' },
    {
      label: '', key: 'actions',
      render: (r) => (
        <Link
          to={`/inward/${r._id}`}
          className="inline-flex items-center gap-1.5 text-xs text-zip-muted hover:text-zip-red transition-colors font-mono"
        >
          <Eye size={13} /> View
        </Link>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      {/* Filters + action */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <div className="flex gap-3 flex-1">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zip-muted" />
            <input
              type="text"
              placeholder="Search invoice…"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="zip-input pl-9"
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="zip-input w-44"
          >
            <option value="">All Statuses</option>
            <option value="received">Received</option>
            <option value="stored">Stored</option>
          </select>
        </div>
        {can('inward.create') && (
          <button
            onClick={() => navigate('/inward/new')}
            className="btn-primary flex items-center gap-2 whitespace-nowrap"
          >
            <Plus size={15} /> New Receipt
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="zip-card p-5 space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-10 skeleton rounded" />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={receipts}
          emptyText="No inward receipts found. Create your first receipt."
        />
      )}
    </div>
  );
};

export default InwardList;
